import React from "react";
import {
  Typography,
  Box,
  Fab,
  Select,
  MenuItem,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  CircularProgress,
  Tooltip,
  DialogActions,
} from "@mui/material";
import { Attachment, CheckCircleOutlineRounded } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { MobileDateTimePicker } from "@mui/x-date-pickers";
import InfoIcon from "@mui/icons-material/Info";

import { BootstrapTooltip } from "../StyledComponents";
import { documentsType, assessmentsType } from "../constants";
import { ACM } from "../../../constants/UserRoles";
import { formatSubRoleLabel } from "../../../utils/roleDisplayUtils";

const DoctorOrTeacherUploadForm = ({ user, typeOfUser, hook }) => {
  const {
    uploadedFile,
    isLoading,
    handleFileChange,
    date,
    dateChangeHandler,
    typeOfDocument,
    setTypeOfDocument,
    setOtherDocumentType,
    typeOfAssessment,
    setTypeOfAssessment,
    setOtherAssessmentType,
    selectedStudent,
    setSelectedStudent,
    SelectedPatient,
    SetSelectedPatient,
    mergedData,
    truncateName,
    extractDocumentsFromGoals,
    setExtractDocumentsFromGoals,
    information,
    setInformation,
    isPreRegistered,
    shareWithTeacherIds,
    setShareWithTeacherIds,
    teachers,
    error,
    handleClose,
    attachmentSaveHandler,
  } = hook;

  return (
    <>
      <Typography gutterBottom sx={{ textAlign: "center", mt: "0.3rem" }}>
        Upload attachment for review
      </Typography>
      <Box className="flexBox flexCenter">
        <BootstrapTooltip title="choose a file" placement="right">
          <Fab
            variant="extended"
            component="label"
            color="secondary"
            size="small"
          >
            <Attachment />
            <input
              hidden
              accept=".pdf"
              multiple
              type="file"
              onChange={handleFileChange}
              disabled={isLoading}
            />
          </Fab>
        </BootstrapTooltip>
      </Box>
      <Typography className="flexBox flexCenter" color={"GrayText"}>
        {uploadedFile?.name && (
          <>
            {uploadedFile?.name}
            <CheckCircleOutlineRounded color="success" />
          </>
        )}
      </Typography>
      <Typography gutterBottom className="dateInput">
        * Date of attachment created:
      </Typography>
      <LocalizationProvider dateAdapter={AdapterMoment}>
        <MobileDateTimePicker
          label="Date of Attachment Created"
          disableFuture
          className="fullWidth"
          value={date || null}
          disabled={isLoading}
          onChange={dateChangeHandler}
          renderInput={(params) => <TextField {...params} />}
        />
      </LocalizationProvider>

      <Typography gutterBottom className="dateInput">
        * Type of Document
      </Typography>
      <Select
        value={typeOfDocument}
        className="fullWidth"
        onChange={(e) => setTypeOfDocument(e.target.value)}
        disabled={isLoading}
        MenuProps={{
          PaperProps: {
            style: {
              boxShadow:
                "0px 3px 6px -2px rgba(0,0,0,0.2), 0px 6px 16px 0px rgba(0,0,0,0.14), 0px 1.5px 5px 0px rgba(0,0,0,0.12)",
            },
          },
        }}
      >
        {documentsType?.map((data) => (
          <MenuItem key={data.value} value={data.value}>
            {data.label}
          </MenuItem>
        ))}
      </Select>

      {typeOfDocument === "Other" && (
        <TextField
          variant="outlined"
          size="medium"
          label="Specify document type"
          fullWidth
          onChange={(e) => setOtherDocumentType(e.target.value)}
          disabled={isLoading}
          sx={{ mt: 2 }}
        />
      )}
      {typeOfDocument === "Assessment" && (
        <>
          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
            * Type of Assessment
          </Typography>
          <Select
            value={typeOfAssessment}
            fullWidth
            onChange={(e) => setTypeOfAssessment(e.target.value)}
            disabled={isLoading}
          >
            {assessmentsType.map((data) => (
              <MenuItem key={data.value} value={data.value}>
                {data.label}
              </MenuItem>
            ))}
          </Select>

          {typeOfAssessment === "Other" && (
            <TextField
              variant="outlined"
              size="medium"
              label="Specify assessment type"
              fullWidth
              onChange={(e) => setOtherAssessmentType(e.target.value)}
              disabled={isLoading}
              sx={{ mt: 2 }}
            />
          )}
        </>
      )}
      <Typography gutterBottom>
        {ACM[typeOfUser]?.isTeacher ? "Students List" : "Patients List"}
      </Typography>
      <Select
        value={ACM[typeOfUser]?.isTeacher ? selectedStudent : SelectedPatient}
        className="fullWidth"
        onChange={(e) =>
          ACM[typeOfUser]?.isTeacher
            ? setSelectedStudent(e.target.value)
            : SetSelectedPatient(e.target.value)
        }
        disabled={isLoading}
      >
        {(Array.isArray(mergedData) ? [...mergedData] : [])
          .sort((a, b) =>
            (a?.fullname || a?.name || "")
              .toLowerCase()
              .localeCompare((b?.fullname || b?.name || "").toLowerCase())
          )
          .map((item) => (
            <MenuItem key={item.id} value={item?.id}>
              <BootstrapTooltip
                title={
                  item?.parentName
                    ? `${item.fullname || item.name}(${item.parentName})`
                    : item?.fullname || item.name
                }
              >
                {item?.parentName
                  ? `${truncateName(
                      item.fullname || item.name
                    )} (${truncateName(item.parentName)})`
                  : `${truncateName(item.fullname || item.name)}`}
              </BootstrapTooltip>
            </MenuItem>
          ))}
      </Select>
      {typeOfDocument !== "Assessment" && (
        <Box sx={{ width: "100%", mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={extractDocumentsFromGoals}
                onChange={(e) => setExtractDocumentsFromGoals(e.target.checked)}
              />
            }
            label="Extract goals from document content"
          />
        </Box>
      )}

      <Typography gutterBottom>Notes:</Typography>
      <TextField
        className="notes fullWidth fullHeight"
        variant="outlined"
        size="medium"
        label="Please input notes..."
        sx={{ my: 0.5 }}
        multiline
        rows={6}
        InputProps={{
          style: {
            minHeight: "55px",
          },
        }}
        onChange={(e) =>
          setInformation({ ...information, notes: e.target.value })
        }
        disabled={isLoading}
      />

      {!isPreRegistered(selectedStudent) && selectedStudent && (
        <FormControlLabel
          control={
            <Checkbox
              checked={shareWithTeacherIds.includes("Parent/Guardian")}
              onChange={(e) => {
                if (e.target.checked) {
                  setShareWithTeacherIds([
                    ...shareWithTeacherIds,
                    "Parent/Guardian",
                  ]);
                } else {
                  setShareWithTeacherIds(
                    shareWithTeacherIds.filter((id) => id !== "Parent/Guardian")
                  );
                }
              }}
            />
          }
          label={"Share with Parent/Guardian"}
        />
      )}
      {teachers?.length > 0 &&
        teachers.map((teacher, index) => {
          const subRole = teacher?.info?.subRoleName;
          const subRoleLabel = formatSubRoleLabel(subRole);
          const showTooltip = Boolean(subRoleLabel);

          return (
            <FormControlLabel
              key={teacher?.uid || index}
              control={
                <Checkbox
                  checked={shareWithTeacherIds.includes(teacher?.uid)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setShareWithTeacherIds([
                        ...shareWithTeacherIds,
                        teacher?.uid,
                      ]);
                    } else {
                      setShareWithTeacherIds(
                        shareWithTeacherIds.filter((id) => id !== teacher?.uid)
                      );
                    }
                  }}
                  value={teacher?.uid}
                />
              }
              label={
                <span>
                  Share with {teacher?.info?.fullname}
                  {showTooltip && (
                    <Tooltip title={subRoleLabel} arrow placement="top">
                      <InfoIcon
                        sx={{
                          fontSize: "small",
                          ml: 0.5,
                          verticalAlign: "middle",
                          color: "action.active",
                          cursor: "help",
                        }}
                      />
                    </Tooltip>
                  )}
                </span>
              }
              sx={{ mr: 2 }}
            />
          );
        })}
      {error.show && (
        <Typography color={"red"} sx={{ py: 2 }}>
          {error.message}
        </Typography>
      )}
      <DialogActions
        className="fullWidth flexBox flexWrap"
        sx={{
          alignItems: "center",
          justifyContent: "center",
          gap: 1,
          mt: 3,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          sx={{
            width: "48%",
            fontFamily: "Lato",
            textTransform: "capitalize",
            color: "var(--clr-theme-purple)",
            borderColor: "var(--clr-theme-purple)",
          }}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          sx={{
            alignItems: "center",
            backgroundColor: "var(--clr-theme-purple)!important",
            color: "white",
            width: "48%",
            fontFamily: "Lato",
            textTransform: "capitalize",
          }}
          variant="contained"
          onClick={attachmentSaveHandler}
          disabled={isLoading}
          startIcon={
            isLoading ? (
              <CircularProgress size={16} sx={{ color: "white" }} />
            ) : null
          }
        >
          {isLoading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </>
  );
};

export default DoctorOrTeacherUploadForm;
